import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getToken } from '../../lib/auth'
import { getSecurityOverview, changePassword, listSecurityDevices, removeSecurityDevice, type SecurityOverview, type SecurityDevice } from '../../api/security'
import { getRecoveryKitStatus, startRecoveryKitExport, fetchRecoveryMnemonic, confirmRecoveryKit, type RecoveryKitStatus } from '../../api/wallet'
import SecurityStatusCard from '../../components/settings/SecurityStatusCard'
import SecurityDevicesCard from '../../components/settings/SecurityDevicesCard'
import { shouldHandleRecoveryKitHash } from './securityHashNavigation'

export default function SecuritySettings() {
  const location = useLocation()
  const lastHandledHashRef = useRef<string | null>(null)
  const authed = Boolean(getToken())
  const [ov, setOv] = useState<SecurityOverview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<SecurityDevice[] | null>(null)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [oldPwd, setOldPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [changing, setChanging] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [rk, setRk] = useState<RecoveryKitStatus | null>(null)
  const [rkLoading, setRkLoading] = useState(false)
  const [rkMsg, setRkMsg] = useState<string | null>(null)
  const [reauthOpen, setReauthOpen] = useState(false)
  const [reauthPwd, setReauthPwd] = useState('')
  const [reauthOtp, setReauthOtp] = useState('')
  const [exportToken, setExportToken] = useState<string | null>(null)
  const [mnemonicOpen, setMnemonicOpen] = useState(false)
  const [mnemonicAck, setMnemonicAck] = useState(false)
  const [mnemonicVisible, setMnemonicVisible] = useState(false)
  const [copyMsg, setCopyMsg] = useState<string | null>(null)
  const [pwdOpen, setPwdOpen] = useState(false)
  const [recPwd, setRecPwd] = useState('')
  const [recPwd2, setRecPwd2] = useState('')
  const [words, setWords] = useState<string[] | null>(null)
  const [wordCheck, setWordCheck] = useState<{ a: number; b: number; aVal: string; bVal: string } | null>(null)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    if (!authed) return
    setLoading(true)
    Promise.all([getSecurityOverview(), listSecurityDevices()])
      .then(([o, devs]) => { setOv(o); setDevices(devs) })
      .catch((e) => setError(e?.message || 'Failed to load security overview'))
      .finally(() => setLoading(false))
    setRkLoading(true)
    getRecoveryKitStatus()
      .then(setRk)
      .catch(() => {})
      .finally(() => setRkLoading(false))
  }, [authed])

  useEffect(() => {
    if (location.hash !== '#recovery-kit') {
      lastHandledHashRef.current = null
      return
    }
    if (!shouldHandleRecoveryKitHash(location.hash, lastHandledHashRef.current)) return

    let cancelled = false
    let attempts = 0
    const maxAttempts = 20

    const scrollToRecoveryKit = () => {
      if (cancelled) return
      const el = document.getElementById('recovery-kit')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Accessible post-scroll focus target for keyboard/screen-reader users.
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1')
        ;(el as HTMLElement).focus({ preventScroll: true })
        lastHandledHashRef.current = location.hash
        return
      }

      attempts += 1
      if (attempts < maxAttempts) {
        window.setTimeout(scrollToRecoveryKit, 75)
      }
    }

    scrollToRecoveryKit()
    return () => {
      cancelled = true
    }
  }, [location.hash])

  async function onChangePassword() {
    setChanging(true)
    setMsg(null)
    try {
      const payload: any = { new_password: newPwd }
      if (ov?.has_password) payload.old_password = oldPwd
      await changePassword(payload)
      setMsg('Password updated')
      setOldPwd(''); setNewPwd('')
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Change failed')
    } finally {
      setChanging(false)
    }
  }

  async function onRemoveDevice(d: SecurityDevice) {
    try {
      setRemovingId(d.id)
      await removeSecurityDevice(d.id)
      // refresh devices + overview counters
      const [o, devs] = await Promise.all([getSecurityOverview(), listSecurityDevices()])
      setOv(o)
      setDevices(devs)
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Failed to remove device')
    } finally {
      setRemovingId(null)
    }
  }

  function bufToB64(buf: ArrayBuffer) {
    const bytes = new Uint8Array(buf)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary)
  }

  async function encryptAndDownload(mnemonic: string, password: string) {
    const enc = new TextEncoder()
    const salt = crypto.getRandomValues(new Uint8Array(16))
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey'])
    const key = await crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: 390000, hash: 'SHA-256' },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    )
    const payload = JSON.stringify({ mnemonic })
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(payload))

    const kit = {
      schema: 'pixeloop.recovery.v1',
      createdAt: new Date().toISOString(),
      wallet: {
        ss58: rk?.wallet_ss58 || '',
        network: rk?.wallet_network || 'pixe',
        ss58Prefix: 42,
      },
      crypto: {
        cipher: 'AES-256-GCM',
        kdf: 'PBKDF2-SHA256',
        iterations: 390000,
        saltB64: bufToB64(salt.buffer),
        ivB64: bufToB64(iv.buffer),
      },
      payloadB64: bufToB64(ciphertext),
    }

    const blob = new Blob([JSON.stringify(kit, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'pixeloop-recovery.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function startExport() {
    setRkMsg(null)
    try {
      const res = await startRecoveryKitExport({ password: reauthPwd, otp_code: reauthOtp || undefined })
      setExportToken(res.export_token_id)
      setReauthOpen(false)
      setReauthPwd(''); setReauthOtp('')
      const m = await fetchRecoveryMnemonic({ export_token_id: res.export_token_id })
      const w = m.mnemonic.trim().split(/\s+/)
      setWords(w)
      setMnemonicAck(false)
      setMnemonicVisible(false)
      setCopyMsg(null)
      setMnemonicOpen(true)
    } catch (e: any) {
      setRkMsg(e?.response?.data?.detail || e?.message || 'Export start failed')
    }
  }

  async function handleGenerateKit() {
    if (!recPwd || recPwd !== recPwd2) {
      setRkMsg('Recovery password mismatch')
      return
    }
    try {
      if (!words || words.length === 0) {
        setRkMsg('Mnemonic missing. Please re-authenticate.')
        return
      }
      await encryptAndDownload(words.join(' '), recPwd)
      const a = Math.max(1, Math.min(words.length, Math.floor(Math.random() * words.length) + 1))
      const b = Math.max(1, Math.min(words.length, Math.floor(Math.random() * words.length) + 1))
      setWordCheck({ a, b, aVal: '', bVal: '' })
      setMnemonicVisible(false)
      setCopyMsg(null)
      setPwdOpen(false)
    } catch (e: any) {
      setRkMsg(e?.response?.data?.detail || e?.message || 'Failed to fetch mnemonic')
    }
  }

  async function confirmBackup() {
    if (!wordCheck || !words) return
    const okA = wordCheck.aVal.trim().toLowerCase() === words[wordCheck.a - 1]
    const okB = wordCheck.bVal.trim().toLowerCase() === words[wordCheck.b - 1]
    if (!okA || !okB) {
      setRkMsg('Word check failed. Please verify your recovery kit.')
      return
    }
    setConfirming(true)
    try {
      await confirmRecoveryKit({ confirmed: true, method: 'recovery_kit' })
      setRkMsg('Recovery kit confirmed')
      const status = await getRecoveryKitStatus()
      setRk(status)
      // best-effort wipe
      setWords(null)
      setWordCheck(null)
      setRecPwd(''); setRecPwd2(''); setExportToken(null)
    } catch (e: any) {
      setRkMsg(e?.response?.data?.detail || e?.message || 'Confirm failed')
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-extrabold">Security</h1>
      {loading && <div className="rounded-xl border border-white/10 p-4">Loading…</div>}
      {error && <div className="rounded-xl border border-white/10 p-4 text-red-400">{error}</div>}
      {ov && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SecurityStatusCard overview={ov} />
          <div className="rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-3">Change password</div>
            {ov.has_password && (
              <label className="block mb-2">
                <div className="text-sm text-white/70 mb-1">Current password</div>
                <input type="password" value={oldPwd} onChange={(e)=>setOldPwd(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
              </label>
            )}
            <label className="block mb-3">
              <div className="text-sm text-white/70 mb-1">New password</div>
              <input type="password" value={newPwd} onChange={(e)=>setNewPwd(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <button onClick={onChangePassword} disabled={changing || !newPwd} className="px-4 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-60">{changing ? 'Updating…' : 'Update password'}</button>
            {msg && <div className="mt-2 text-sm text-white/85">{msg}</div>}
          </div>
          <div className="md:col-span-2">
            <SecurityDevicesCard devices={devices || []} onRemove={onRemoveDevice} removingId={removingId} />
          </div>
          <div id="recovery-kit" className="md:col-span-2 rounded-xl border border-white/10 p-4">
            <div className="font-semibold mb-2">Recovery Kit</div>
            <div className="text-sm text-white/70 mb-4">Generate an encrypted recovery kit for your wallet. The password never leaves your device.</div>
            {rkLoading && <div className="text-sm text-white/70">Loading status…</div>}
            {rk && (
              <div className="text-sm text-white/75 mb-3">
                <div>Wallet SS58: {rk.wallet_ss58 || '—'}</div>
                <div>Backup confirmed: {rk.backup_confirmed_at ? new Date(rk.backup_confirmed_at).toLocaleString() : 'Not confirmed'}</div>
                <div>Last kit generated: {rk.last_recovery_kit_generated_at ? new Date(rk.last_recovery_kit_generated_at).toLocaleString() : 'Never'}</div>
              </div>
            )}
            <button onClick={() => setReauthOpen(true)} className="px-4 py-2 rounded-lg bg-white text-neutral-950 font-semibold">
              {rk?.last_recovery_kit_generated_at ? 'Regenerate Recovery Kit' : 'Generate Recovery Kit'}
            </button>
            {rkMsg && <div className="mt-2 text-sm text-rose-300">{rkMsg}</div>}
          </div>
        </div>
      )}

      {reauthOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-neutral-900 p-5">
            <div className="font-semibold mb-2">Re-authenticate</div>
            <div className="text-sm text-white/70 mb-4">Enter your password. If you have SMS verification enabled, you can also enter the OTP code.</div>
            <label className="block mb-3">
              <div className="text-sm text-white/70 mb-1">Password</div>
              <input type="password" value={reauthPwd} onChange={(e)=>setReauthPwd(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <label className="block mb-4">
              <div className="text-sm text-white/70 mb-1">OTP code (optional)</div>
              <input type="text" value={reauthOtp} onChange={(e)=>setReauthOtp(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{ setReauthOpen(false); setReauthPwd(''); setReauthOtp('') }} className="px-3 py-2 rounded-lg border border-white/10 text-white/80">Cancel</button>
              <button onClick={startExport} disabled={!reauthPwd} className="px-3 py-2 rounded-lg bg-brand text-white font-semibold disabled:opacity-60">Continue</button>
            </div>
          </div>
        </div>
      )}

      {mnemonicOpen && words && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-900 p-5">
            <div className="font-semibold mb-2">Your Recovery Phrase (shown once)</div>
            <div className="text-sm text-white/70 mb-4">
              Write these words down. Anyone with this phrase can access your funds. You will not be shown this again unless you re-authenticate.
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-white/60">{mnemonicVisible ? 'Visible' : 'Hidden'}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMnemonicVisible((v) => !v)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-white/80 text-xs"
                >
                  {mnemonicVisible ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(words.join(' '))
                      setCopyMsg('Copied')
                    } catch {
                      setCopyMsg('Copy failed')
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-white/80 text-xs"
                >
                  Copy
                </button>
              </div>
            </div>
            {copyMsg && <div className="text-xs text-white/60 mb-2">{copyMsg}</div>}
            <div className="grid grid-cols-3 gap-2 text-sm bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
              {words.map((w, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-white/40">{i + 1}.</span>
                  <span className="font-mono">{mnemonicVisible ? w : '••••••'}</span>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-white/80 mb-4">
              <input type="checkbox" checked={mnemonicAck} onChange={(e)=>setMnemonicAck(e.target.checked)} />
              I understand anyone with these words can steal funds.
            </label>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setMnemonicOpen(false)
                  setMnemonicAck(false)
                  setMnemonicVisible(false)
                  setCopyMsg(null)
                  setWords(null)
                  setExportToken(null)
                }}
                className="px-3 py-2 rounded-lg border border-white/10 text-white/80"
              >
                Cancel
              </button>
              <button
                onClick={() => { setMnemonicOpen(false); setPwdOpen(true) }}
                disabled={!mnemonicAck}
                className="px-3 py-2 rounded-lg bg-brand text-white font-semibold disabled:opacity-60"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {pwdOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-neutral-900 p-5">
            <div className="font-semibold mb-2">Set Recovery Password</div>
            <div className="text-sm text-white/70 mb-4">This password encrypts your recovery kit locally. We do not store it.</div>
            <label className="block mb-3">
              <div className="text-sm text-white/70 mb-1">Recovery password</div>
              <input type="password" value={recPwd} onChange={(e)=>setRecPwd(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <label className="block mb-4">
              <div className="text-sm text-white/70 mb-1">Confirm password</div>
              <input type="password" value={recPwd2} onChange={(e)=>setRecPwd2(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{ setPwdOpen(false); setRecPwd(''); setRecPwd2(''); setWords(null); setExportToken(null); }} className="px-3 py-2 rounded-lg border border-white/10 text-white/80">Cancel</button>
              <button onClick={handleGenerateKit} className="px-3 py-2 rounded-lg bg-brand text-white font-semibold">Download kit</button>
            </div>
          </div>
        </div>
      )}

      {wordCheck && words && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-neutral-900 p-5">
            <div className="font-semibold mb-2">Confirm Recovery Kit</div>
            <div className="text-sm text-white/70 mb-4">Enter two words from your recovery phrase to confirm you saved it.</div>
            <label className="block mb-3">
              <div className="text-sm text-white/70 mb-1">Word #{wordCheck.a}</div>
              <input type="text" value={wordCheck.aVal} onChange={(e)=>setWordCheck({ ...wordCheck, aVal: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <label className="block mb-4">
              <div className="text-sm text-white/70 mb-1">Word #{wordCheck.b}</div>
              <input type="text" value={wordCheck.bVal} onChange={(e)=>setWordCheck({ ...wordCheck, bVal: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{ setWordCheck(null); setWords(null); setExportToken(null); }} className="px-3 py-2 rounded-lg border border-white/10 text-white/80">Skip</button>
              <button onClick={confirmBackup} disabled={confirming} className="px-3 py-2 rounded-lg bg-brand text-white font-semibold disabled:opacity-60">{confirming ? 'Confirming…' : 'Confirm backup'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
